import { Rng } from '../core/rng';
import { clamp, smoothstep, Vec3 } from '../core/math';
import { MASTS, deckHalfWidth, deckY } from '../data/ship';
import { DamageModel, sectionAt } from './damage';
import type { SimEvents } from './events';
import type { Ship } from './ship';

// Fire on board. The ship is divided into cells over two levels (the hold and
// the weather deck), three across. Burning cells consume their wood, damage
// the structure around them, and spread to neighbours, much faster downwind
// and upward; rain, spray and firefighting parties knock them back. Fire that
// reaches the magazine ends the matter.

export interface FireCell {
  /** Design-space centre. */
  x: number;
  y: number;
  z: number;
  level: 0 | 1;
  station: number;
  lane: number;
  fuel: number;
  heat: number;
  wet: number;
  /** Men with buckets on it right now (set by the crew each step). */
  fighters: number;
}

const STATIONS = 12;
const X0 = -16.5, X1 = 17.5;

export class FireSystem {
  readonly cells: FireCell[] = [];
  /** Number of cells alight. */
  burning = 0;
  /** Largest heat anywhere, 0..1. */
  worst = 0;
  private readonly rng: Rng;

  constructor(seed: number) {
    this.rng = new Rng(seed);
    for (let level = 0; level < 2; level++) {
      for (let s = 0; s < STATIONS; s++) {
        const x = X0 + ((s + 0.5) / STATIONS) * (X1 - X0);
        const hw = Math.max(0.8, deckHalfWidth(x));
        for (let lane = 0; lane < 3; lane++) {
          const z = (lane - 1) * hw * 0.62;
          this.cells.push({
            x, y: level === 0 ? -1.2 : deckY(x) + 0.5, z, level: level as 0 | 1, station: s, lane,
            fuel: 1, heat: 0, wet: 0, fighters: 0,
          });
        }
      }
    }
  }

  cellAt(level: number, station: number, lane: number): FireCell | undefined {
    if (station < 0 || station >= STATIONS || lane < 0 || lane > 2) return undefined;
    return this.cells[level * STATIONS * 3 + station * 3 + lane];
  }

  /** Nearest cell to a design-space point. */
  nearest(x: number, y: number, z: number): FireCell {
    const level = y < deckY(x) - 0.3 ? 0 : 1;
    const s = clamp(Math.floor(((x - X0) / (X1 - X0)) * STATIONS), 0, STATIONS - 1);
    const hw = Math.max(0.8, deckHalfWidth(x));
    const lane = clamp(Math.round(z / (hw * 0.62)) + 1, 0, 2);
    return this.cellAt(level, s, lane)!;
  }

  ignite(x: number, y: number, z: number, amount: number, ship: Ship | null, events: SimEvents | null, vessel = 0): void {
    const c = this.nearest(x, y, z);
    if (c.fuel <= 0.05) return;
    const was = c.heat;
    c.heat = clamp(c.heat + amount * (1 - c.wet * 0.7), 0, 1);
    if (was < 0.05 && c.heat >= 0.05 && ship && events) {
      const p = ship.designToWorld(tmp.set(c.x, c.y, c.z), tmp);
      events.push({ type: 'fire', x: p.x, y: p.y, z: p.z, vessel, started: true });
    }
  }

  update(dt: number, ship: Ship, damage: DamageModel, rain: number, windLocalX: number, windLocalZ: number, events: SimEvents, vessel: number): void {
    const wl = Math.hypot(windLocalX, windLocalZ) || 1;
    const wx = windLocalX / wl, wz = windLocalZ / wl;
    const windK = clamp(wl / 10, 0.2, 2.5);
    let burning = 0, worst = 0;
    const ignitions: FireCell[] = [];
    for (const c of this.cells) {
      // Spray and green water soak the weather deck; rain soaks everything up top.
      if (c.level === 1) c.wet = clamp(c.wet + (rain * 0.05 + ship.deckWater * 0.3) * dt, 0, 1);
      c.wet = clamp(c.wet - 0.004 * dt * (1 + c.heat * 4), 0, 1);
      if (c.heat <= 0.01) {
        c.heat = 0;
        continue;
      }
      burning++;
      worst = Math.max(worst, c.heat);
      // Fire grows while it has wood and air; buckets and rain beat it down.
      // Embers without enough heat to take hold die away on their own.
      const grow = 0.03 * c.fuel * (1 - c.wet * 0.8) * (0.6 + 0.4 * windK) * (c.level === 0 ? 0.7 : 1) * smoothstep(0.02, 0.1, c.heat);
      const douse = rain * 0.05 * (c.level === 1 ? 1 : 0) + c.fighters * 0.028 + c.wet * 0.02 + 0.003;
      // Buckets and wetted sails soak the timbers so they don't catch again.
      c.wet = clamp(c.wet + c.fighters * 0.015 * dt, 0, 1);
      c.heat = clamp(c.heat + (grow * (1 - c.heat * 0.5) - douse) * dt, 0, 1);
      c.fuel = Math.max(0, c.fuel - c.heat * dt / 260);
      if (c.fuel <= 0.02) c.heat *= 1 - dt * 0.2;

      // Burning weakens planking, decks and frames around it.
      const sec = sectionAt(c.x);
      const side = c.lane === 1 ? (this.rng.next() < 0.5 ? -1 : 1) : c.lane === 0 ? -1 : 1;
      const d = c.heat * dt;
      if (c.level === 1) damage.deck[sec]! -= d * 0.6;
      else damage.cargo[sec] = Math.max(0, damage.cargo[sec]! - d * 0.01);
      if (c.lane !== 1) damage.damageHull(sec, side, d * 0.25, null);
      damage.structure[sec]! -= d * 0.12;
      // Long, hot fires burn through the side.
      if (c.lane !== 1 && c.heat > 0.7 && c.fuel < 0.5 && this.rng.chance(0.02 * dt)) {
        const hb = Math.max(0.5, deckHalfWidth(c.x));
        damage.addHole(c.x + this.rng.range(-1, 1), c.level === 0 ? this.rng.range(-0.8, 0.9) : c.y - 0.3, side * (hb + 0.2), this.rng.range(0.2, 0.45), true);
      }

      // Spread: to the neighbours, upward, and (strongly) downwind.
      const spread = c.heat * 0.02 * windK;
      for (const [ds, dl, dv] of [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]] as const) {
        const n = this.cellAt(c.level + dv, c.station + ds, c.lane + dl);
        if (!n || n.fuel < 0.05 || n.heat > 0.3) continue;
        const dirx = ds, dirz = dl;
        const len = Math.hypot(dirx, dirz) || 1;
        const down = dv !== 0 ? 0.5 + (dv > 0 ? 1.5 : 0) : 1 + 2 * Math.max(0, (dirx * wx + dirz * wz) / len) * windK;
        const p = spread * down * n.fuel * (1 - n.wet * 0.9) * dt;
        if (this.rng.chance(p)) ignitions.push(n);
      }

      // Flames climbing a mast take the rigging and set the canvas alight.
      if (c.level === 1) {
        for (const m of MASTS) {
          const md = damage.masts[m.id];
          if (!md || md.lostLevel >= 3 || Math.abs(m.x - c.x) > 2.2) continue;
          md.hp -= c.heat * dt * 0.35;
          damage.damageRigging(m.id, c.heat * dt * 0.012);
          if (this.rng.chance(c.heat * 0.04 * dt)) {
            for (const s of ship.sails.sails) {
              if (s.lost || s.mount.mast !== m.id || s.deploy < 0.1) continue;
              s.cloth.burn(0.04 * c.heat, () => this.rng.next());
            }
          }
        }
      }
      // Heat reaching the powder room.
      if (c.level === 0 && Math.abs(c.x - damage.magazine.x) < 4 && c.heat > 0.4) {
        damage.magazine.heat += (c.heat - 0.4) * dt * 0.02;
      }
    }
    for (const n of ignitions) {
      const was = n.heat;
      n.heat = Math.max(n.heat, 0.12);
      if (was < 0.05) {
        const p = ship.designToWorld(tmp.set(n.x, n.y, n.z), tmp);
        events.push({ type: 'fire', x: p.x, y: p.y, z: p.z, vessel, started: true });
      }
    }
    for (const c of this.cells) c.fighters = 0;
    this.burning = burning;
    this.worst = worst;
  }
}

const tmp = new Vec3();
